import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Kicker } from '@/components/Kicker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { OfferManagerDialog } from '@/components/OfferManagerDialog';
import { toast } from 'sonner';

export default function AdminUsers() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'users'], queryFn: async () => (await api.get('/admin/users')).data });
  const partnersQ = useQuery({ queryKey: ['admin', 'partners'], queryFn: async () => (await api.get('/admin/partners')).data });
  const [openFor, setOpenFor] = useState(null);
  const [editCatsFor, setEditCatsFor] = useState(null);
  const [catsInput, setCatsInput] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', full_name: '', company: '' });
  const [offersFor, setOffersFor] = useState(null);

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post('/admin/users', payload)).data,
    onSuccess: () => {
      toast.success(t('admin.users.created'));
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      setCreateOpen(false);
      setNewUser({ email: '', password: '', full_name: '', company: '' });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t('auth.errors.generic')),
  });
  const delMut = useMutation({
    mutationFn: async (userId) => (await api.delete(`/admin/users/${userId}`)).data,
    onSuccess: () => { toast.success('OK'); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || t('auth.errors.generic')),
  });

  const linkMut = useMutation({
    mutationFn: async ({ userId, odoo_partner_id }) =>
      (await api.post(`/admin/users/${userId}/link-partner`, { odoo_partner_id })).data,
    onSuccess: () => { toast.success('OK'); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); setOpenFor(null); },
    onError: () => toast.error(t('auth.errors.generic')),
  });
  const unlinkMut = useMutation({
    mutationFn: async (userId) => (await api.post(`/admin/users/${userId}/unlink-partner`, {})).data,
    onSuccess: () => { toast.success('OK'); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
  });
  const setCatsMut = useMutation({
    mutationFn: async ({ userId, cats }) => (await api.post(`/admin/users/${userId}/categories`, { partner_category_names: cats })).data,
    onSuccess: () => { toast.success('OK'); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); setEditCatsFor(null); },
  });

  const partners = partnersQ.data?.items || [];
  const users = data?.items || [];

  return (
    <section>
      <Kicker>{t('admin.title')}</Kicker>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-semibold text-[#14171C]">{t('admin.nav.users')}</h1>
        <button onClick={() => setCreateOpen(true)} data-testid="admin-user-create" className="pn-btn-orange !py-2 !px-4">
          {t('admin.users.create')}
        </button>
      </div>

      <div className="mt-8 pn-card overflow-hidden">
        <table className="w-full text-left text-sm" data-testid="admin-users-table">
          <thead className="bg-[#F6F8FB] text-xs uppercase tracking-wide text-[#4A4D52]">
            <tr>
              <th className="px-4 py-3">E-Mail</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">{t('admin.users.role')}</th>
              <th className="px-4 py-3">{t('admin.users.partner')}</th>
              <th className="px-4 py-3">{t('admin.users.categories')}</th>
              <th className="px-4 py-3 text-right">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="p-6 text-center text-[#4A4D52]">{t('common.loading')}</td></tr>}
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#E4E9F0] hover:bg-[#F6F8FB]">
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">{u.full_name}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${u.role === 'admin' ? 'bg-[#FBEADD] text-[#E2611C]' : 'bg-[#F6F8FB] text-[#14171C]'}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3">
                  {u.odoo_partner_name ? (
                    <span className="text-[#14171C]"><span className="font-mono text-xs text-[#4A4D52]">#{u.odoo_partner_id}</span> {u.odoo_partner_name}</span>
                  ) : (
                    <span className="text-[#4A4D52]">{t('admin.users.not_linked')}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">
                  {(u.partner_category_names || []).length === 0 ? <span className="text-[#4A4D52]">—</span> : (u.partner_category_names || []).map((c) => (
                    <span key={c} className="mr-1 inline-flex rounded-full bg-[#F6F8FB] px-2 py-0.5">{c}</span>
                  ))}
                </td>
                <td className="px-4 py-3 text-right">
                  {u.role !== 'admin' && (
                    <div className="inline-flex gap-2">
                      <button onClick={() => setOffersFor(u)} data-testid="admin-user-offers" className="rounded-full bg-[#E2611C] px-3 py-1 text-xs font-semibold text-white hover:brightness-95">
                        {t('offers.manage_for_user')}
                      </button>
                      <button onClick={() => setOpenFor(u)} data-testid="admin-user-link" className="rounded-full border border-[#E4E9F0] bg-white px-3 py-1 text-xs font-semibold hover:border-[#E2611C]/40">
                        {t('admin.users.link')}
                      </button>
                      <button onClick={() => { setEditCatsFor(u); setCatsInput((u.partner_category_names || []).join(', ')); }} data-testid="admin-user-edit-cats" className="rounded-full border border-[#E4E9F0] bg-white px-3 py-1 text-xs font-semibold hover:border-[#E2611C]/40">
                        {t('admin.users.edit_categories')}
                      </button>
                      {u.odoo_partner_id && (
                        <button onClick={() => unlinkMut.mutate(u.id)} className="rounded-full border border-[#E4E9F0] bg-white px-3 py-1 text-xs font-semibold text-[#B42318] hover:border-[#B42318]/40">
                          {t('admin.users.unlink')}
                        </button>
                      )}
                      <button
                        onClick={() => { if (window.confirm(t('admin.users.confirm_delete', { email: u.email }))) delMut.mutate(u.id); }}
                        data-testid="admin-user-delete"
                        className="rounded-full border border-[#E4E9F0] bg-white px-3 py-1 text-xs font-semibold text-[#B42318] hover:border-[#B42318]/40"
                      >
                        {t('admin.users.delete')}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.users.create')}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <input placeholder={t('auth.full_name')} className="h-11 rounded-lg border border-[#E4E9F0] bg-white px-3 text-sm outline-none focus:border-[#E2611C]" value={newUser.full_name} onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })} data-testid="admin-create-name" />
            <input placeholder={t('auth.company')} className="h-11 rounded-lg border border-[#E4E9F0] bg-white px-3 text-sm outline-none focus:border-[#E2611C]" value={newUser.company} onChange={(e) => setNewUser({ ...newUser, company: e.target.value })} data-testid="admin-create-company" />
            <input type="email" placeholder={t('auth.email')} className="h-11 rounded-lg border border-[#E4E9F0] bg-white px-3 text-sm outline-none focus:border-[#E2611C]" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="admin-create-email" />
            <input type="password" placeholder={t('auth.password')} className="h-11 rounded-lg border border-[#E4E9F0] bg-white px-3 text-sm outline-none focus:border-[#E2611C]" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="admin-create-password" />
            <div className="text-xs text-[#4A4D52]">{t('admin.users.create_hint')}</div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setCreateOpen(false)} className="pn-btn-outline !py-2 !px-4">{t('admin.users.cancel')}</button>
            <button
              onClick={() => createMut.mutate(newUser)}
              disabled={!newUser.email || !newUser.password || newUser.password.length < 8 || !newUser.full_name}
              className="pn-btn-orange !py-2 !px-4 disabled:opacity-60"
              data-testid="admin-create-submit"
            >
              {t('admin.users.save')}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!openFor} onOpenChange={(v) => !v && setOpenFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.users.pick_partner')}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[420px] overflow-auto">
            {partners.map((p) => (
              <button
                key={p._odoo_id}
                onClick={() => linkMut.mutate({ userId: openFor.id, odoo_partner_id: p._odoo_id })}
                data-testid={`admin-partner-pick-${p._odoo_id}`}
                className="flex w-full items-center justify-between border-b border-[#E4E9F0] px-3 py-3 text-left text-sm hover:bg-[#F6F8FB]"
              >
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="font-mono text-[11px] text-[#4A4D52]">#{p._odoo_id} {p.email || ''}</div>
                </div>
                <div className="text-[11px] text-[#4A4D52]">
                  {(p.category_names || []).join(', ') || '—'}
                </div>
              </button>
            ))}
            {partners.length === 0 && <div className="p-4 text-sm text-[#4A4D52]">{t('catalog.empty')}</div>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editCatsFor} onOpenChange={(v) => !v && setEditCatsFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.users.edit_categories')}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#4A4D52]">{t('admin.users.cats_hint')}</p>
          <input value={catsInput} onChange={(e) => setCatsInput(e.target.value)} data-testid="admin-cats-input" className="h-11 w-full rounded-lg border border-[#E4E9F0] bg-white px-3 text-sm outline-none focus:border-[#E2611C]" placeholder="Customer ABC, TPU" />
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setEditCatsFor(null)} className="pn-btn-outline !py-2 !px-4">{t('admin.users.cancel')}</button>
            <button onClick={() => setCatsMut.mutate({ userId: editCatsFor.id, cats: catsInput.split(',').map((s) => s.trim()).filter(Boolean) })} data-testid="admin-cats-save" className="pn-btn-orange !py-2 !px-4">{t('admin.users.save')}</button>
          </div>
        </DialogContent>
      </Dialog>

      <OfferManagerDialog
        open={!!offersFor}
        onOpenChange={(v) => !v && setOffersFor(null)}
        mode="by-user"
        target={offersFor}
      />
    </section>
  );
}
